/**
 * Máscara de preço/embarcador para visão de transportadora (Lances).
 *
 * Contrato preferencial (API):
 * - priceVisibility.isMasked
 * - priceVisibility.displayPrice
 * - priceVisibility.displayShipperName
 * - priceVisibility.priceSource
 *
 * UI: isMasked === true → card cinza + ações desabilitadas.
 * Fallback local só quando a API não envia priceVisibility.isMasked.
 */

export const PRICE_SOURCE = {
  TOTAL: "TOTAL",
  TON: "TON",
  ANNOUNCED: "ANNOUNCED",
};

function asNonEmptyString(value) {
  if (value == null) return "";
  const str = String(value).trim();
  return str || "";
}

function parseMoney(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number(String(value).replace(/\./g, "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Embarcador cliente/pagador do frete (preferência) ou dono do post.
 */
export function extractFreightOwnerShipperId(item) {
  if (!item || typeof item !== "object") return "";

  const clientPayment =
    item.clientPayment ?? item.client_payment ?? item.client ?? {};
  const shipperObj = item.shipper;
  const shipperUid =
    shipperObj && typeof shipperObj === "object"
      ? shipperObj.uid ?? shipperObj.id ?? ""
      : typeof shipperObj === "string"
        ? shipperObj
        : "";

  return (
    asNonEmptyString(item.shipperUid) ||
    asNonEmptyString(clientPayment?.uid) ||
    asNonEmptyString(clientPayment?.id) ||
    asNonEmptyString(shipperUid) ||
    asNonEmptyString(item.idShipper) ||
    asNonEmptyString(item.shipperId) ||
    ""
  );
}

export function extractAssignedTransport(item) {
  if (!item || typeof item !== "object") {
    return { idTransport: "", name: "" };
  }

  const transport =
    (item.transport && typeof item.transport === "object" && item.transport) ||
    (item.freight?.transport &&
      typeof item.freight.transport === "object" &&
      item.freight.transport) ||
    {};

  return {
    idTransport: asNonEmptyString(
      transport.idTransport ?? transport.id ?? item.idTransport,
    ),
    name: asNonEmptyString(
      transport.name ??
        transport.companyNameFantasy ??
        transport.nameFantasy ??
        item.acceptedCarrierName,
    ),
  };
}

export function extractAnnouncedValue(item) {
  if (!item || typeof item !== "object") return null;

  const driver =
    (item.driver && typeof item.driver === "object" && item.driver) ||
    (item.freight?.driver &&
      typeof item.freight.driver === "object" &&
      item.freight.driver) ||
    {};

  return parseMoney(
    item.announcedValue ??
      item.valueAnnounced ??
      driver.valueFinal ??
      item.valueFinal ??
      item.freight?.valueFinal,
  );
}

export function extractRealShipperName(item, fallback = "—") {
  if (!item || typeof item !== "object") return fallback;

  const shipperObj = item.shipper;
  const shipperNameFromObj =
    shipperObj && typeof shipperObj === "object"
      ? shipperObj.name ??
        shipperObj.nameFantasy ??
        shipperObj.socialName ??
        shipperObj.dataPersonal?.nameFantasy ??
        shipperObj.dataPersonal?.socialName
      : typeof shipperObj === "string"
        ? shipperObj
        : "";

  return (
    asNonEmptyString(item.shipperName) ||
    asNonEmptyString(item.clientPaymentName) ||
    asNonEmptyString(item.clientPayment?.name) ||
    asNonEmptyString(shipperNameFromObj) ||
    asNonEmptyString(item.embarcador) ||
    fallback
  );
}

function normalizeViewerIds(viewerShipperIds) {
  if (!Array.isArray(viewerShipperIds)) return [];
  return Array.from(
    new Set(viewerShipperIds.map((id) => asNonEmptyString(id)).filter(Boolean)),
  );
}

/** UI: card cinza / ações desabilitadas quando isMasked. */
export function isPriceVisibilityMasked(freight) {
  if (!freight || typeof freight !== "object") return false;
  if (freight.isMasked === true) return true;
  return freight.priceVisibility?.isMasked === true;
}

const ANONYMOUS_SHIPPER_HINT =
  "Embarcador anônimo, contate a Fortio para mais informações";

/** Detecta texto de embarcador mascarado/anônimo vindo da API. */
export function isAnonymousShipperLabel(name) {
  const text = asNonEmptyString(name).toLowerCase();
  if (!text) return false;
  return (
    text.includes("anônim") ||
    text.includes("anonim") ||
    text.includes("contate a fortio") ||
    text === ANONYMOUS_SHIPPER_HINT.toLowerCase()
  );
}

export function getAnonymousShipperBannerText(name) {
  const text = asNonEmptyString(name);
  if (!text) return ANONYMOUS_SHIPPER_HINT;
  return isAnonymousShipperLabel(text) ? text : ANONYMOUS_SHIPPER_HINT;
}

/**
 * @param {object} item - Item bruto da API
 * @param {object} [options]
 * @param {string[]} [options.viewerShipperIds]
 * @param {number|null} [options.totalPrice]
 * @param {string} [options.pricingType]
 * @param {string} [options.fallbackShipperName]
 */
export function resolveCarrierPriceVisibility(item, options = {}) {
  const viewerShipperIds = normalizeViewerIds(options.viewerShipperIds);
  const totalPrice =
    options.totalPrice != null
      ? parseMoney(options.totalPrice)
      : parseMoney(
          item?.freightValue ??
            item?.valueFreightage ??
            item?.freight?.valueFreightage,
        );
  const pricingType = asNonEmptyString(options.pricingType) || "Total";
  const fallbackShipperName = options.fallbackShipperName || "—";
  const realShipperName = extractRealShipperName(item, fallbackShipperName);
  const assigned = extractAssignedTransport(item);
  const hasAssignedTransport = Boolean(assigned.idTransport);

  const fromApi = item?.priceVisibility;
  if (fromApi && typeof fromApi === "object" && fromApi.isMasked != null) {
    const isMasked = Boolean(fromApi.isMasked);
    return {
      isMasked,
      displayPrice:
        parseMoney(fromApi.displayPrice) ??
        (isMasked ? extractAnnouncedValue(item) : totalPrice),
      displayShipperName:
        asNonEmptyString(fromApi.displayShipperName) ||
        (isMasked ? assigned.name || realShipperName : realShipperName),
      priceSource:
        asNonEmptyString(fromApi.priceSource) ||
        (isMasked
          ? PRICE_SOURCE.ANNOUNCED
          : pricingType.toLowerCase() === "ton"
            ? PRICE_SOURCE.TON
            : PRICE_SOURCE.TOTAL),
      fromApi: true,
      hasAssignedTransport,
      isFromViewerGroup: !isMasked,
    };
  }

  // Fallback local apenas se a API não enviar o contrato.
  const ownerShipperId = extractFreightOwnerShipperId(item);
  const isFromViewerGroup =
    !ownerShipperId ||
    viewerShipperIds.length === 0 ||
    viewerShipperIds.includes(ownerShipperId);
  const shouldMask = !isFromViewerGroup && hasAssignedTransport;

  if (shouldMask) {
    return {
      isMasked: true,
      displayPrice: extractAnnouncedValue(item) ?? totalPrice,
      displayShipperName: assigned.name || realShipperName,
      priceSource: PRICE_SOURCE.ANNOUNCED,
      fromApi: false,
      hasAssignedTransport,
      isFromViewerGroup,
    };
  }

  return {
    isMasked: false,
    displayPrice: totalPrice,
    displayShipperName: realShipperName,
    priceSource:
      pricingType.toLowerCase() === "ton"
        ? PRICE_SOURCE.TON
        : PRICE_SOURCE.TOTAL,
    fromApi: false,
    hasAssignedTransport,
    isFromViewerGroup,
  };
}
